<script setup lang="ts">
import { PRODUCTS_ROUTES } from '@/router/routes'
import { EProductActions } from '@/store/productStore/ProductStoreTypes'
import { EStoreModules } from '@/store/storeType'
import {
    DeleteButton,
    DividerItem,
    FullScreenLoader,
    HrefWrapper,
    InformationBaseCard,
    MenuListItem,
    PrimaryButton,
    StackLayout,
    TitleTypography,
    TypographyWithPrefix
} from '@rfc/rfc-components-library'
import { useDetailModel } from '@rfc/rfc-components-library/hooks'
import {
    EOrientation,
    type DeleteButtonConfirmationDialog
} from '@rfc/rfc-components-library/types'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ERouteId } from '../../../types/enum/RouteIdEnum'
import type { ProductModel } from '../../../types/models/ProductModel'
import ProductEditDialog from '../../../views/ProductsView/components/ProductEditDialog.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const productId = computed(() => route.params[ERouteId.PRODUCTS_ID].toString())

const editDialogOpen = ref(false)

const productModel = useDetailModel<ProductModel>({
    store,
    id: productId.value,
    stateKey: EStoreModules.PRODUCT,
    getStateAction: EProductActions.FETCH_PRODUCT,
    updateStateAction: EProductActions.UPDATE_PRODUCT
})

const confirmationDialog: DeleteButtonConfirmationDialog = {
    title: t('customers.delete'),
    contentText: t('customers.deleteConfirm')
}
const handleConfirm = async () => {
    if (!productModel.data.value) return
    store
        .dispatch(`${EStoreModules.PRODUCT}/${EProductActions.DELETE_PRODUCT}`, {
            id: productModel.data.value?.id,
            name: productModel.data.value?.name
        })
        .then(() => router.replace(PRODUCTS_ROUTES.baseUrl))
}
</script>

<template>
    <StackLayout direction="column" :gap="16" class="w-full h-full pa-4">
        <StackLayout class="align-center justify-space-between">
            <StackLayout :gap="8" class="align-center">
                <HrefWrapper icon="mid mdi-arrow-left" :href="PRODUCTS_ROUTES.baseUrl" />
                <TitleTypography :title="$t('products.detail')" />
            </StackLayout>
            <StackLayout v-if="productModel.storedData.value" :gap="8">
                <PrimaryButton :label="$t('common.edit')" @click="editDialogOpen = true" />
                <DeleteButton
                    :label="$t('products.delete')"
                    :confirmationDialog="confirmationDialog"
                    @click="handleConfirm" />
            </StackLayout>
        </StackLayout>

        <FullScreenLoader :loading="productModel.loading.value" />

        <!--
            TP-257 : le dashboard mène ici depuis une erreur `product`, dont la
            cause est justement la suppression. Sans cette bannière, l'admin
            verrait une fiche d'apparence normale et ne comprendrait pas ce
            qu'on attend de lui.
        -->
        <div
            v-if="productModel.data.value?.deleted_at"
            data-test="product-deleted-banner"
            class="product-deleted">
            {{ $t('orders.orderErrors.deletedProduct') }}
        </div>

        <InformationBaseCard v-if="productModel.data.value" :label="productModel.data.value?.name">
            <StackLayout direction="column" :gap="8" class="w-full">
                <TypographyWithPrefix
                    :prefix="$t('products.columns.id')"
                    :label="productModel.data.value?.id"
                    boldLabel />
                <DividerItem :orientation="EOrientation.HORIZONTAL" />
                <TypographyWithPrefix
                    :prefix="$t('products.columns.productId')"
                    :label="productModel.data.value?.product_id"
                    boldLabel />
                <DividerItem :orientation="EOrientation.HORIZONTAL" />
                <TypographyWithPrefix
                    :prefix="$t('products.columns.name')"
                    :label="productModel.data.value?.name"
                    boldLabel />
                <DividerItem :orientation="EOrientation.HORIZONTAL" />
                <TypographyWithPrefix
                    :prefix="$t('products.columns.encoding')"
                    :label="productModel.storedData.value?.encoding"
                    boldLabel />
                <DividerItem :orientation="EOrientation.HORIZONTAL" />
                <TypographyWithPrefix
                    :prefix="$t('products.columns.rfidChipTemplate')"
                    :label="productModel.storedData.value?.rfid_chip_template?.name ?? '—'"
                    boldLabel />
                <DividerItem :orientation="EOrientation.HORIZONTAL" />
                <TypographyWithPrefix
                    :prefix="$t('products.columns.endRollLabelTemplate')"
                    :label="productModel.storedData.value?.end_roll_label_template?.name ?? '—'"
                    boldLabel />
            </StackLayout>
        </InformationBaseCard>

        <ProductEditDialog
            :open="!!editDialogOpen"
            :data="productModel.data.value ?? {}"
            @change="productModel.handleUpdateValue"
            @confirm="productModel.saveModifications"
            @close="editDialogOpen = false" />
    </StackLayout>
</template>

<style scoped>
.product-deleted {
    border-left: 4px solid rgb(var(--v-theme-error));
    padding: 12px 16px;
    font-weight: 600;
}
</style>
